// src/utils/successionDossierPdf.js
// Succession Dossier (§4 TRANSMIT, §12 notaires) — document préparatoire de transmission
// pour une collection. Inventaire + détention (pleine propriété / usufruit / nue-propriété)
// + attribution par bénéficiaire (source : artwork_beneficiary) + œuvres exceptionnelles.
// ARTKIV prépare le dossier ; le notaire/les professionnels habilités valident (§12/§13).
// Langues : fr (défaut), en, de — paramètre `lang`. Les libellés sont dans le dictionnaire T ci-dessous.
// jsPDF v3 : import jsPDF from "jspdf" + autoTable(doc, {...}).
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { BRAND_UPPER } from "../lib/rebrand";
import { valuationFooter, valuationDate, isStaleValuation } from "./valuationDisclosure";

const LANGS = ["fr", "en", "de"];
const pickLang = (lang) => {
  const l = String(lang || "fr").slice(0, 2).toLowerCase();
  return LANGS.includes(l) ? l : "fr";
};
const LOCALE = { fr: "fr-FR", en: "en-GB", de: "de-DE" };

const T = {
  fr: {
    notSet: "à renseigner",
    ownership: { full: "Pleine propriété", usufruct: "Usufruit", bare_ownership: "Nue-propriété" },
    untitled: "(sans titre)",
    unknownArtist: "artiste non renseigné",
    headerTag: "DOSSIER DE SUCCESSION — DOCUMENT PRÉPARATOIRE",
    collectionFallback: "Collection",
    holder: "Titulaire",
    summary: ["Nombre d'œuvres", "Valeur totale estimée", "Œuvres exceptionnelles", "Œuvres avec bénéficiaire désigné"],
    inventoryTitle: "Inventaire & attribution",
    inventoryHead: ["Œuvre", "Artiste", "Valeur (indicative)", "Estimée le", "Détention", "Bénéficiaire(s)"],
    old: " (ancienne)",
    splitTitle: "Répartition par bénéficiaire",
    splitHead: ["Bénéficiaire", "Œuvres", "Valeur attribuée", "Part"],
    unassigned: "Non attribué",
    starNote: "* œuvre exceptionnelle. En cas de co-attribution, la valeur n'est pas divisée automatiquement.",
    digitalTitle: "Œuvres numériques et accès associés",
    digitalNote: "ARTICHAUT ne conserve ni mot de passe, ni clé privée, ni phrase de récupération : seules figurent la localisation de l'accès, son dépositaire et la procédure.",
    certDeclared: "Certificat d'authenticité déclaré",
    steps: [
      (p) => (p ? `Suivre la procédure indiquée : ${p}` : "Procédure d'accès à renseigner."),
      () => "Faire confirmer par la plateforme, par écrit, ce qui est transmis (compte, œuvre, droits).",
      () => "Faire actualiser la valeur avant tout partage ou toute revente.",
    ],
    sections: { work: "L'œuvre", cert: "Le certificat", account: "Le compte permettant d'y accéder", rights: "Les droits acquis", value: "Valeur indicative", heirs: "Démarches à effectuer par les héritiers" },
    rows: {
      format: "Média et format", platform: "Plateforme source", platformId: "Identifiant chez la plateforme",
      sourceUrl: "Page de l'œuvre sur la plateforme", sourceCollection: "Collection thématique", importedOn: "Importée le",
      certificate: "Certificat ou preuve d'authenticité", acqDate: "Date d'acquisition", acqPrice: "Prix d'acquisition",
      custodian: "Qui détient l'accès", procedure: "Procédure à suivre", usage: "Usage et affichage",
      equipment: "Équipement d'affichage", resale: "Conditions de revente", transmission: "Conditions connues de transmission",
      estimate: "Estimation", heirInstruction: "Instruction laissée aux héritiers",
    },
    asOf: (d) => ` (au ${d})`,
    digitalFoot: "Valeurs indicatives à la date indiquée : elles ne constituent pas une expertise opposable.",
    foot: (brand, date) =>
      `Document préparatoire établi via ${brand} le ${date}. Il ne se substitue ni à l'acte notarié, ni à l'expertise, ni au conseil fiscal. ${brand} structure la donnée ; le notaire et les professionnels habilités valident ce qui relève de leur compétence.`,
    fileSlug: "dossier-succession",
  },
  en: {
    notSet: "to be completed",
    ownership: { full: "Full ownership", usufruct: "Usufruct", bare_ownership: "Bare ownership" },
    untitled: "(untitled)",
    unknownArtist: "artist not specified",
    headerTag: "SUCCESSION FILE — PREPARATORY DOCUMENT",
    collectionFallback: "Collection",
    holder: "Holder",
    summary: ["Number of works", "Total estimated value", "Exceptional works", "Works with a named beneficiary"],
    inventoryTitle: "Inventory & allocation",
    inventoryHead: ["Work", "Artist", "Value (indicative)", "Estimated on", "Holding", "Beneficiary(ies)"],
    old: " (old)",
    splitTitle: "Breakdown by beneficiary",
    splitHead: ["Beneficiary", "Works", "Allocated value", "Share"],
    unassigned: "Unallocated",
    starNote: "* exceptional work. Where a work is shared between several beneficiaries, its value is not divided automatically.",
    digitalTitle: "Digital artworks and related access",
    digitalNote: "ARTICHAUT does not keep any password, private key or recovery phrase: only where the access is kept, who holds it and the procedure to follow are recorded.",
    certDeclared: "Certificate of authenticity declared",
    steps: [
      (p) => (p ? `Follow the stated procedure: ${p}` : "Access procedure to be completed."),
      () => "Ask the platform to confirm in writing what is being transferred (account, artwork, rights).",
      () => "Have the value updated before any sharing or resale.",
    ],
    sections: { work: "The artwork", cert: "The certificate", account: "The account that gives access", rights: "Rights acquired", value: "Indicative value", heirs: "Steps for the heirs" },
    rows: {
      format: "Medium and format", platform: "Source platform", platformId: "Platform identifier",
      sourceUrl: "Artwork page on the platform", sourceCollection: "Thematic collection", importedOn: "Imported on",
      certificate: "Certificate or proof of authenticity", acqDate: "Date of acquisition", acqPrice: "Acquisition price",
      custodian: "Who holds the access", procedure: "Procedure to follow", usage: "Usage and display",
      equipment: "Display equipment", resale: "Resale conditions", transmission: "Known transfer conditions",
      estimate: "Estimate", heirInstruction: "Instruction left for the heirs",
    },
    asOf: (d) => ` (as of ${d})`,
    digitalFoot: "Indicative values at the date shown: they are not an enforceable appraisal.",
    foot: (brand, date) =>
      `Preparatory document produced with ${brand} on ${date}. It does not replace a notarial deed, an appraisal or tax advice. ${brand} organises the data; the notary and other qualified professionals validate whatever falls within their competence.`,
    fileSlug: "succession-file",
  },
  de: {
    notSet: "noch auszufüllen",
    ownership: { full: "Volleigentum", usufruct: "Nießbrauch", bare_ownership: "Nacktes Eigentum" },
    untitled: "(ohne Titel)",
    unknownArtist: "Künstler nicht angegeben",
    headerTag: "NACHLASSAKTE — VORBEREITENDES DOKUMENT",
    collectionFallback: "Sammlung",
    holder: "Inhaber",
    summary: ["Anzahl der Werke", "Geschätzter Gesamtwert", "Außergewöhnliche Werke", "Werke mit benanntem Begünstigten"],
    inventoryTitle: "Inventar & Zuordnung",
    inventoryHead: ["Werk", "Künstler", "Wert (Richtwert)", "Geschätzt am", "Besitzform", "Begünstigte(r)"],
    old: " (veraltet)",
    splitTitle: "Aufteilung nach Begünstigten",
    splitHead: ["Begünstigte(r)", "Werke", "Zugeordneter Wert", "Anteil"],
    unassigned: "Nicht zugeordnet",
    starNote: "* außergewöhnliches Werk. Bei Mehrfachzuordnung wird der Wert nicht automatisch geteilt.",
    digitalTitle: "Digitale Kunstwerke und zugehöriger Zugang",
    digitalNote: "ARTICHAUT speichert weder Passwörter noch private Schlüssel noch Wiederherstellungsphrasen: Festgehalten werden nur der Ort des Zugangs, wer ihn verwahrt und das einzuhaltende Verfahren.",
    certDeclared: "Echtheitszertifikat angegeben",
    steps: [
      (p) => (p ? `Das angegebene Verfahren befolgen: ${p}` : "Zugangsverfahren noch auszufüllen."),
      () => "Die Plattform schriftlich bestätigen lassen, was übertragen wird (Konto, Werk, Rechte).",
      () => "Den Wert vor jeder Weitergabe oder jedem Weiterverkauf aktualisieren lassen.",
    ],
    sections: { work: "Das Werk", cert: "Das Zertifikat", account: "Das Konto, das den Zugang ermöglicht", rights: "Erworbene Rechte", value: "Richtwert", heirs: "Schritte der Erben" },
    rows: {
      format: "Medium und Format", platform: "Quellplattform", platformId: "Kennung bei der Plattform",
      sourceUrl: "Seite des Werks auf der Plattform", sourceCollection: "Thematische Sammlung", importedOn: "Importiert am",
      certificate: "Zertifikat oder Echtheitsnachweis", acqDate: "Erwerbsdatum", acqPrice: "Erwerbspreis",
      custodian: "Wer den Zugang verwahrt", procedure: "Einzuhaltendes Verfahren", usage: "Nutzung und Anzeige",
      equipment: "Anzeigegerät", resale: "Bedingungen für den Weiterverkauf", transmission: "Bekannte Übertragungsbedingungen",
      estimate: "Schätzung", heirInstruction: "Anweisung an die Erben",
    },
    asOf: (d) => ` (Stand ${d})`,
    digitalFoot: "Richtwerte zum angegebenen Datum: Sie stellen kein rechtsverbindliches Gutachten dar.",
    foot: (brand, date) =>
      `Vorbereitendes Dokument, erstellt mit ${brand} am ${date}. Es ersetzt weder eine notarielle Urkunde noch ein Gutachten noch eine steuerliche Beratung. ${brand} strukturiert die Daten; der Notar und die zuständigen Fachleute prüfen, was in ihre Zuständigkeit fällt.`,
    fileSlug: "nachlassakte",
  },
};

const currentValue = (a) =>
  Number(a?.current_estimated_value ?? a?.valuation_estimated_value ?? a?.estimated_value ?? a?.value ?? 0) || 0;
const isDigitalArtwork = (a) => a?.medium_category === "nft" || (a?.digital_asset && typeof a.digital_asset === "object") || !!a?.external_source;

// Chapitre « Œuvres numériques et accès associés » : distingue l'œuvre, le certificat, le compte qui permet
// d'y accéder, les droits acquis, la valeur indicative et les démarches des héritiers.
// Ne contient jamais de secret d'accès : seulement où se trouve l'accès, qui le détient et la procédure.
// Renvoie la nouvelle position verticale ; n'écrit rien s'il n'y a aucune œuvre numérique.
function addDigitalChapter(doc, artworks, startY, M, lang = "fr") {
  const L = T[pickLang(lang)];
  const loc = LOCALE[pickLang(lang)];
  const eur = (v) => (v || v === 0 ? `${Number(v).toLocaleString(loc)} €` : "—");
  const text = (v) => (v == null || String(v).trim() === "" ? L.notSet : String(v).trim());
  const money = (v, cur) => (v || v === 0 ? `${Number(v).toLocaleString(loc)} ${String(cur || "EUR").trim()}` : L.notSet);
  const dateOf = (d) => new Date(d).toLocaleDateString(loc);

  const digital = artworks.filter(isDigitalArtwork);
  if (digital.length === 0) return startY - 10;

  let y = startY;
  if (y > 240) { doc.addPage(); y = 18; }
  doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(25);
  doc.text(L.digitalTitle, M, y);
  doc.setFont("helvetica", "italic"); doc.setFontSize(8); doc.setTextColor(120);
  doc.text(L.digitalNote, M, y + 4.5, { maxWidth: 178 });
  y += 10;

  const sectionRow = (label) => [{ content: label, colSpan: 2, styles: { fillColor: [245, 242, 235], fontStyle: "bold" } }];

  for (const a of digital) {
    const d = a.digital_asset && typeof a.digital_asset === "object" ? a.digital_asset : {};
    const certificate = a.certificate_authenticity || (a.certificate_auth ? L.certDeclared : null);
    const value = currentValue(a);
    const valueDate = valuationDate(a);
    const heirSteps = [L.steps[0](d.access_procedure), L.steps[1](), L.steps[2]()];

    if (y > 225) { doc.addPage(); y = 18; }
    autoTable(doc, {
      startY: y, margin: { left: M, right: M }, theme: "grid",
      styles: { fontSize: 8.5, cellPadding: 1.6, valign: "top" },
      columnStyles: { 0: { cellWidth: 52, fontStyle: "bold", textColor: 90 } },
      head: [[{ content: `${a.title || L.untitled} — ${a.artist || L.unknownArtist}`, colSpan: 2 }]],
      headStyles: { fillColor: [26, 26, 26], fontSize: 9 },
      body: [
        sectionRow(L.sections.work),
        [L.rows.format, text(d.format)],
        [L.rows.platform, text(a.external_source)],
        [L.rows.platformId, text(a.external_id)],
        ...(a.source_url ? [[L.rows.sourceUrl, a.source_url]] : []),
        ...(a.source_collection ? [[L.rows.sourceCollection, a.source_collection]] : []),
        ...(a.imported_at ? [[L.rows.importedOn, dateOf(a.imported_at)]] : []),
        sectionRow(L.sections.cert),
        [L.rows.certificate, text(certificate)],
        [L.rows.acqDate, a.acquisition_date ? dateOf(a.acquisition_date) : L.notSet],
        [L.rows.acqPrice, money(a.acquisition_price, a.acquisition_currency || a.currency)],
        sectionRow(L.sections.account),
        [L.rows.custodian, text(d.access_custodian)],
        [L.rows.procedure, text(d.access_procedure)],
        sectionRow(L.sections.rights),
        [L.rows.usage, text(d.usage_rights)],
        [L.rows.equipment, text(d.display_equipment)],
        [L.rows.resale, text(d.resale_conditions)],
        [L.rows.transmission, text(d.transmission_conditions)],
        sectionRow(L.sections.value),
        [L.rows.estimate, value ? `${eur(value)}${valueDate ? L.asOf(dateOf(valueDate)) : ""}` : L.notSet],
        sectionRow(L.sections.heirs),
        [{ content: heirSteps.map((s, i) => `${i + 1}. ${s}`).join("\n"), colSpan: 2 }],
        ...(d.heir_instructions ? [[L.rows.heirInstruction, d.heir_instructions]] : []),
      ],
    });
    y = (doc.lastAutoTable?.finalY || y) + 6;
  }

  doc.setFont("helvetica", "italic"); doc.setFontSize(7.5); doc.setTextColor(140);
  doc.text(L.digitalFoot, M, y);
  return y + 4;
}

// benByArtwork : { [artwork_id]: [{ name, relation }] }
export function generateSuccessionDossier({ collectionName = "", ownerName = "", artworks = [], benByArtwork = {}, lang = "fr" } = {}) {
  const lg = pickLang(lang);
  const L = T[lg];
  const loc = LOCALE[lg];
  const eur = (v) => (v || v === 0 ? `${Number(v).toLocaleString(loc)} €` : "—");

  const doc = new jsPDF("p", "mm", "a4");
  const M = 16;
  let y = 18;

  const total = artworks.length;
  const totalValue = artworks.reduce((s, a) => s + currentValue(a), 0);
  const exceptional = artworks.filter((a) => a?.is_exceptional === true);
  const withBen = artworks.filter((a) => (benByArtwork[a.id] || []).length > 0);

  // ---- En-tête ----
  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(150);
  doc.text(L.headerTag, M, y);
  doc.setFontSize(18); doc.setTextColor(20);
  y += 9; doc.text(doc.splitTextToSize(collectionName || L.collectionFallback, 180), M, y);
  doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(90);
  y += 6; if (ownerName) { doc.text(`${L.holder} : ${ownerName}`, M, y); y += 5; }

  // ---- Synthèse ----
  autoTable(doc, {
    startY: y + 2, theme: "plain", margin: { left: M, right: M },
    styles: { fontSize: 10, cellPadding: 1.6 },
    columnStyles: { 0: { fontStyle: "bold", textColor: 120, cellWidth: 70 } },
    body: [
      [L.summary[0], String(total)],
      [L.summary[1], eur(totalValue)],
      [L.summary[2], String(exceptional.length)],
      [L.summary[3], `${withBen.length} / ${total}`],
    ],
  });
  y = (doc.lastAutoTable?.finalY || y) + 8;

  // ---- Inventaire & attribution ----
  doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(25);
  doc.text(L.inventoryTitle, M, y);
  autoTable(doc, {
    startY: y + 3, margin: { left: M, right: M },
    head: [L.inventoryHead],
    body: artworks
      .slice()
      .sort((a, b) => currentValue(b) - currentValue(a))
      .map((a) => [
        (a.is_exceptional ? "* " : "") + (a.title || L.untitled),
        a.artist || "—",
        eur(currentValue(a)),
        valuationDate(a) ? new Date(valuationDate(a)).toLocaleDateString(loc) + (isStaleValuation(valuationDate(a)) ? L.old : "") : "—",
        L.ownership[a.ownership_status] || a.ownership_status || "—",
        (benByArtwork[a.id] || []).map((b) => b.name + (b.relation ? ` (${b.relation})` : "")).join(", ") || "—",
      ]),
    headStyles: { fillColor: [26, 26, 26], fontSize: 8.5 },
    styles: { fontSize: 8.5, cellPadding: 1.6 },
    columnStyles: { 2: { halign: "right" } },
  });
  y = (doc.lastAutoTable?.finalY || y) + 8;

  // ---- Répartition par bénéficiaire ----
  const byBen = new Map(); // name -> { relation, count, value }
  let unassignedCount = 0, unassignedValue = 0;
  for (const a of artworks) {
    const bens = benByArtwork[a.id] || [];
    if (bens.length === 0) { unassignedCount += 1; unassignedValue += currentValue(a); continue; }
    for (const b of bens) {
      const cur = byBen.get(b.name) || { relation: b.relation || "", count: 0, value: 0 };
      cur.count += 1; cur.value += currentValue(a); // valeur non divisée si co-attribution
      byBen.set(b.name, cur);
    }
  }
  const rows = [...byBen.entries()].map(([name, v]) => [
    name + (v.relation ? ` (${v.relation})` : ""),
    String(v.count),
    eur(v.value),
    totalValue ? `${Math.round((v.value / totalValue) * 100)} %` : "—",
  ]);
  if (unassignedCount) rows.push([L.unassigned, String(unassignedCount), eur(unassignedValue), totalValue ? `${Math.round((unassignedValue / totalValue) * 100)} %` : "—"]);

  if (y > 245) { doc.addPage(); y = 18; }
  doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(25);
  doc.text(L.splitTitle, M, y);
  autoTable(doc, {
    startY: y + 3, margin: { left: M, right: M },
    head: [L.splitHead],
    body: rows.length ? rows : [["—", "0", "—", "—"]],
    headStyles: { fillColor: [26, 26, 26], fontSize: 9 },
    styles: { fontSize: 9, cellPadding: 1.8 },
    columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" } },
  });
  y = (doc.lastAutoTable?.finalY || y) + 3;
  doc.setFont("helvetica", "italic"); doc.setFontSize(7.5); doc.setTextColor(140);
  doc.text(doc.splitTextToSize(L.starNote, 180), M, y + 3);

  // ---- Œuvres numériques et accès associés ----
  y = addDigitalChapter(doc, artworks, y + 10, M, lg);
  if (y > 262) { doc.addPage(); }

  // ---- Pied notaire (§12/§13) ----
  doc.setFont("helvetica", "italic"); doc.setFontSize(7.5); doc.setTextColor(140);
  const foot = valuationFooter(artworks, lg) + " " + L.foot(BRAND_UPPER, new Date().toLocaleDateString(loc));
  doc.text(doc.splitTextToSize(foot, 180), M, 281);

  const slug = (collectionName || "collection").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
  doc.save(`${L.fileSlug}-${slug || "collection"}.pdf`);
}
